import { useState } from 'react';
import { dismiss, report } from '../lib/errorNotices';
import { createFileRoute, Link, useBlocker, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Amount, Button, Chip, Panel, PanelHeader, Ref } from '@promanage/ui';
import { formatNzDate, formatPeriod, transactionKindLabel } from '@promanage/core';
import { TopBar } from '../components/TopBar';
import { FinancialNav } from '../features/ledger/FinancialNav';
import {
  applyResults,
  draftMovement,
  newIdempotencyKey,
  queueTotals,
  toPostInput,
  updateInQueue,
  type AccountOption,
  type DraftFields,
  type DraftTransaction,
} from '../features/ledger/batch';
import { LeaveWarning } from '../features/ledger/LeaveWarning';
import { TransactionFields } from '../features/ledger/TransactionFields';
import { TrustEffectRows } from '../features/ledger/TrustEffectRows';
import { invalidateAccounts } from '../features/ledger/invalidate';
import { errorCode, needsStepUp, trpc } from '../lib/trpc';
import { today } from '../lib/today';

export const Route = createFileRoute('/financial/entry')({
  component: TransactionEntryPage,
});

/**
 * Transaction entry — build a queue, check it, then process it.
 *
 * THE SHAPE OF THE WORK IT IS FOR. A property manager sits down with a bank statement or a
 * morning's post and enters fifteen things. Confirming each one separately means fifteen round
 * trips and fifteen chances to be interrupted halfway; worse, it means the fifteenth is posted
 * before anyone has seen what the fifteen come to. Here the queue is checked as a whole — the
 * debit and credit columns and their effect on the trust balance are the same figures the ledger
 * will show afterwards, computed by the same rule — and then processed in one go.
 *
 * NOTHING HERE HAS TOUCHED THE DATABASE. A draft is a working note. It can be edited or thrown
 * away freely, which is precisely what a posted transaction can never be (invariant 4), and it
 * is why leaving the page warns: the queue exists only in this tab.
 *
 * PROCESSING IS NOT ALL-OR-NOTHING. Each draft posts in its own database transaction, so a
 * refusal — a closed account, an owner who would go negative — holds back that one row and
 * leaves the rest posted, with the reason attached to the row that caused it. Nothing in the
 * ledger's invariants spans two transactions, so this is sound as well as kinder: you fix the
 * one line that was wrong rather than re-checking the fourteen that were right.
 */

/** The one notice a batch reports its refused rows in, replaced by the next batch's. */
const ENTRY_NOTICE = 'entry-queue';
function TransactionEntryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const accounts = useQuery(trpc.ledger.accounts.queryOptions(undefined, { staleTime: 30_000 }));
  const periods = useQuery(trpc.ledger.periods.queryOptions(undefined, { staleTime: 30_000 }));

  const [drafts, setDrafts] = useState<DraftTransaction[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [nextKey, setNextKey] = useState(0);
  // Bumped to remount the form empty after each add, rather than clearing it field by field.
  const [formKey, setFormKey] = useState(0);
  const [outcome, setOutcome] = useState<ProcessOutcome | null>(null);

  const accountList: AccountOption[] = accounts.data ?? [];
  const editing = drafts.find((draft) => draft.key === editingKey) ?? null;
  const totals = queueTotals(drafts, accountList);

  const process = useMutation(
    trpc.ledger.postBatch.mutationOptions({
      meta: {
        errorTitle: 'Could not process the queue',
        /*
         * THE HONEST SENTENCE, and it is not padding. Every draft is still sitting in the queue,
         * one click from being sent again — so if the request DID commit and only the answer was
         * lost, processing again posts all of it twice, onto an append-only ledger where the only
         * correction is a reversing journal. Nothing in the system currently prevents that; see
         * the note on `postBatch`. Until an idempotency key does, the least this screen can do is
         * refuse to claim an outcome it does not know, and point at the one place that has the
         * answer.
         *
         * Stricter than the notices' own rule for a lost answer, which only recognises a dropped
         * connection: here an internal error or a timeout is unknown too, because either can
         * arrive after the handler has already committed several drafts. `outcomeUnknown` below.
         * A refusal that is provably decided before posting keeps the server's own sentence.
         */
        errorDetail: (error) =>
          outcomeUnknown(error)
            ? 'The server never answered, so it is not known whether any of these posted. ' +
              'Check the ledger before processing again — processing a queue that already ' +
              'posted would post it a second time.'
            : null,
      },
      onError(error) {
        // Fifteen minutes since the last code. Not something to explain in a red box — send them
        // to the prompt. The queue is still here when they come back, because it never left.
        if (needsStepUp(error)) void navigate({ to: '/mfa' });
      },
    }),
  );

  /*
   * THE WARNING ON LEAVING. Both halves are needed and they cover different exits:
   * `shouldBlockFn` catches navigation inside the app, `enableBeforeUnload` catches closing the
   * tab, reloading, or the back button leaving the site. A queue is unsaved work by design, so
   * this is the one place in the product where leaving is worth interrupting.
   */
  const dirty = drafts.length > 0;
  const blocker = useBlocker({
    shouldBlockFn: () => dirty,
    enableBeforeUnload: () => dirty,
    withResolver: true,
  });

  const addDraft = (draft: DraftFields) => {
    // The idempotency key is minted HERE, once, at the moment the draft comes into existence.
    setDrafts((current) => [
      ...current,
      { ...draft, key: `d${nextKey}`, idempotencyKey: newIdempotencyKey(), refusal: null },
    ]);
    setNextKey((n) => n + 1);
    setFormKey((n) => n + 1);
    setOutcome(null);
  };

  const updateDraft = (key: string, draft: DraftFields) => {
    // `updateInQueue` is what carries the idempotency key across the edit — see the note there.
    setDrafts((current) => updateInQueue(current, key, draft));
    setEditingKey(null);
    setFormKey((n) => n + 1);
  };

  const removeDraft = (key: string) => {
    setDrafts((current) => current.filter((row) => row.key !== key));
    setOutcome(null);
    if (editingKey === key) {
      setEditingKey(null);
      setFormKey((n) => n + 1);
    }
  };

  /*
   * The banner reports the last Process run, so it is cleared whenever the queue is changed by
   * hand. "18 posted, 2 held back" above a queue somebody has since edited describes a batch
   * that no longer exists, and the refs in it are the part a person would act on.
   */
  const startEditing = (key: string) => {
    setEditingKey(key);
    setOutcome(null);
  };

  /**
   * Send the queue, then rebuild it from what came back.
   *
   * Results are matched to drafts BY KEY, not by position in the queue. The request carries only
   * the drafts that produced a payload, so the two lists can differ in length — and matching a
   * posting result to the wrong row is the kind of mistake that shows an operator a TX number
   * beside a transaction that was refused.
   */
  const runProcess = async () => {
    const sendable = drafts.flatMap((draft) => {
      const input = toPostInput(draft);
      return input === null ? [] : [{ key: draft.key, input }];
    });
    if (sendable.length === 0) return;

    setOutcome(null);

    try {
      const result = await process.mutateAsync({ drafts: sendable.map((item) => item.input) });

      const refusals = new Map<string, readonly string[]>();
      const posted: ProcessOutcome['posted'] = [];

      for (const row of result.results) {
        const key = sendable[row.index]?.key;
        if (key === undefined) continue;
        if (row.ok) posted.push(row.posted);
        else refusals.set(key, row.issues);
      }

      const sent = new Set(sendable.map((item) => item.key));
      setDrafts((current) => applyResults(current, sent, refusals));

      setOutcome({ posted, refusedCount: result.refusedCount });

      /*
       * A BATCH THAT WENT THROUGH CAN STILL HAVE REFUSED ROWS, and the query client never sees
       * those — the request succeeded. Announced here, once; each row keeps its own reason until
       * it is edited, so the notice can be dismissed without losing which row to fix.
       */
      if (refusals.size === 0) {
        dismiss(ENTRY_NOTICE);
      } else {
        report({
          id: ENTRY_NOTICE,
          title: `${String(refusals.size)} of ${String(sendable.length)} ${
            sendable.length === 1 ? 'transaction was' : 'transactions were'
          } not posted`,
          detail: [...refusals.values()].flat().join('\n'),
        });
      }

      // Balances, both lists and possibly a newly-opened period all just changed.
      invalidateAccounts(queryClient);
      void queryClient.invalidateQueries({ queryKey: trpc.ledger.transactions.queryKey() });
      void queryClient.invalidateQueries({ queryKey: trpc.ledger.journals.queryKey() });
      void queryClient.invalidateQueries({ queryKey: trpc.ledger.periods.queryKey() });
    } catch {
      /*
       * THE QUEUE IS LEFT EXACTLY AS IT WAS, which is right for a request that was refused and
       * dangerous for one whose answer was merely lost — the drafts sit there ready to be sent a
       * second time. Nothing is dropped, because dropping a draft that never posted loses an
       * operator's work; instead the notice distinguishes the two cases and refuses to claim an
       * outcome it cannot know. `outcomeUnknown` is that distinction — see `errorDetail` above.
       */
    }
  };

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Transaction entry"
        subtitle={
          drafts.length === 0
            ? 'Queue them up, check the total, then process'
            : `${drafts.length} ${drafts.length === 1 ? 'draft' : 'drafts'} waiting — not yet posted`
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/financial/new">
              <Button size="sm" variant="secondary">
                Post just one
              </Button>
            </Link>
          </div>
        }
      />
      <FinancialNav />

      {outcome ? <ProcessResult outcome={outcome} onDismiss={() => setOutcome(null)} /> : null}

      <div className={outcome ? 'mt-4 flex flex-col gap-4' : 'flex flex-col gap-4'}>
        {/* ── The form ─────────────────────────────────────────────────────── */}
        <TransactionFields
          key={editing ? `edit-${editing.key}-${formKey}` : `add-${formKey}`}
          accounts={accountList}
          periods={periods.data}
          today={today()}
          initial={editing ?? undefined}
          submitLabel={editing ? 'Update draft' : 'Add to queue'}
          onSubmit={(draft) => (editing ? updateDraft(editing.key, draft) : addDraft(draft))}
          {...(editing
            ? {
                onCancel: () => {
                  setEditingKey(null);
                  setFormKey((n) => n + 1);
                },
                cancelLabel: 'Stop editing',
              }
            : {})}
        />

        {/* ── The queue ────────────────────────────────────────────────────── */}
        <Panel className="min-w-0 p-0">
          <PanelHeader
            title="Queued"
            subtitle="Nothing here has posted. Edit or remove any of it"
            className="px-4 pt-4 pb-2"
            aside={
              <Button
                size="sm"
                onClick={() => void runProcess()}
                disabled={drafts.length === 0 || process.isPending}
              >
                {/*
                 * The count is in the button. This is the irreversible step — what it posts
                 * cannot be edited or deleted afterwards — so the label states how much of it
                 * there is rather than leaving the reader to count the rows.
                 */}
                {process.isPending
                  ? 'Processing'
                  : drafts.length === 0
                    ? 'Process'
                    : `Process ${drafts.length} ${drafts.length === 1 ? 'transaction' : 'transactions'}`}
              </Button>
            }
          />

          {drafts.length === 0 ? (
            <p className="px-4 pt-1 pb-5 text-[0.8125rem] text-ink-muted">
              Nothing queued. Fill in the form above and add it — a draft is a working note, and
              posts nothing until you process it.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[46rem] border-collapse text-[0.8125rem]">
                <caption className="px-4 pb-2 text-left text-[0.6875rem] text-ink-subtle">
                  {/*
                   * The same sentence as the posted list, because these are the same two columns
                   * and will become those rows. The convention is the OPPOSITE of the journal
                   * lines in the form above, where a receipt debits the trust bank.
                   */}
                  Debits and credits as the trust account sees them — money in is a credit. The
                  journal lines in the form above use the accounting convention, where a receipt
                  debits the bank.
                </caption>
                <thead>
                  <tr className="border-t border-rule text-left text-[0.6875rem] tracking-wide text-ink-subtle uppercase">
                    <th className="px-4 py-2 font-semibold">Date</th>
                    <th className="px-4 py-2 font-semibold">What happened</th>
                    <th className="px-4 py-2 text-right font-semibold">Debit</th>
                    <th className="px-4 py-2 text-right font-semibold">Credit</th>
                    <th className="px-4 py-2 text-right font-semibold">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {drafts.map((draft) => (
                    <QueueRow
                      key={draft.key}
                      draft={draft}
                      accounts={accountList}
                      editing={draft.key === editingKey}
                      onEdit={() => startEditing(draft.key)}
                      onRemove={() => removeDraft(draft.key)}
                    />
                  ))}
                </tbody>

                <TrustEffectRows
                  debitCents={totals.debitCents}
                  creditCents={totals.creditCents}
                  scope={`${drafts.length} ${drafts.length === 1 ? 'draft' : 'drafts'} · not yet posted`}
                  netLabel="Effect on the trust balance once processed"
                  labelSpan={2}
                  tailSpan={1}
                />
              </table>
            </div>
          )}
        </Panel>
      </div>

      {blocker.status === 'blocked' ? (
        <LeaveWarning count={drafts.length} onStay={blocker.reset} onLeave={blocker.proceed} />
      ) : null}
    </div>
  );
}

/**
 * Codes that are decided BEFORE `postBatch` runs a single draft.
 *
 * A guard refusing the role, a session that has expired, a payload zod rejected, a rate limit —
 * in every one of these the handler body never executed, so the ledger is untouched and saying
 * so is safe.
 */
const DECIDED_BEFORE_POSTING = new Set([
  'UNAUTHORIZED',
  'FORBIDDEN',
  'BAD_REQUEST',
  'TOO_MANY_REQUESTS',
  'PAYLOAD_TOO_LARGE',
]);

/**
 * Whether what happened to the batch is genuinely unknown.
 *
 * Deliberately biased towards "unknown". A dropped connection carries no tRPC code at all, and
 * anything else — an internal error, a timeout, a proxy giving up — arrives after the handler
 * may already have committed several drafts. Only the list above is provably safe, so the
 * default for an unrecognised failure is to warn rather than to reassure.
 */
function outcomeUnknown(error: unknown): boolean {
  const code = errorCode(error);
  return code === undefined || !DECIDED_BEFORE_POSTING.has(code);
}

// ── The queue ────────────────────────────────────────────────────────────────

function QueueRow({
  draft,
  accounts,
  editing,
  onEdit,
  onRemove,
}: {
  draft: DraftTransaction;
  accounts: readonly AccountOption[];
  editing: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const movement = draftMovement(draft, accounts);
  const lines = draft.lines.filter((line) => line.accountId !== '').length;

  return (
    <tr
      className={
        editing
          ? 'border-t border-rule bg-accent-soft/40 align-top'
          : 'border-t border-rule align-top'
      }
    >
      <td className="px-4 py-2.5 tabular-nums whitespace-nowrap">{formatNzDate(draft.date)}</td>

      <td className="min-w-0 px-4 py-2.5">
        <span className="block truncate">{draft.narrative}</span>
        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[0.6875rem] text-ink-subtle">
          <span>
            {transactionKindLabel(draft.kind)} · {lines} {lines === 1 ? 'line' : 'lines'}
          </span>
          {editing ? <Chip tone="accent">Editing</Chip> : null}
        </span>

        {/*
         * The server's own words, on the row that caused them, and kept there until the draft is
         * edited — as a STATUS of the draft. The refusal was announced once, as a notice; a
         * refusal that lived only in a dismissed notice is one an operator would have to remember
         * the row of.
         */}
        {draft.refusal ? (
          <p className="mt-1.5 flex items-start gap-1.5 text-[0.75rem] text-ink-muted">
            <Chip tone="warn" className="shrink-0">
              Not posted
            </Chip>
            <span className="whitespace-pre-line">{draft.refusal.join('\n')}</span>
          </p>
        ) : null}
      </td>

      {/*
       * Blank, not 0.00, on the side this transaction does not move. A column of zeroes is noise
       * to scan past; an empty cell says "not this way" at a glance.
       */}
      <td className="px-4 py-2.5 text-right tabular-nums">
        {movement.debitCents === 0n ? null : <Amount value={movement.debitCents} />}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        {movement.creditCents === 0n ? null : (
          <Amount value={movement.creditCents} tone="positive" />
        )}
      </td>

      <td className="px-4 py-2.5 text-right whitespace-nowrap">
        <Button size="sm" variant="ghost" onClick={onEdit}>
          Edit
        </Button>
        <Button size="sm" variant="ghost" onClick={onRemove}>
          Remove
        </Button>
      </td>
    </tr>
  );
}

// ── After processing ─────────────────────────────────────────────────────────

interface ProcessOutcome {
  posted: {
    id: string;
    refSeq: number;
    amountCents: string;
    amountFormatted: string;
    periodStartsOn: string;
    lateItem: boolean;
    /** Already in the ledger from an earlier attempt; this run wrote nothing for it. */
    replayed: boolean;
  }[];
  refusedCount: number;
}

/**
 * What just happened to the queue.
 *
 * Both numbers, always, and the refused count is not phrased as an error: a batch that posted
 * eighteen and held two back did what it was designed to do. The two are still in the queue
 * below with the server's reason on them, which is where they get fixed.
 */
function ProcessResult({ outcome, onDismiss }: { outcome: ProcessOutcome; onDismiss: () => void }) {
  const posted = outcome.posted.length;
  const replayed = outcome.posted.filter((row) => row.replayed).length;
  const written = posted - replayed;
  const tone = posted === 0 ? 'bg-warn-soft text-warn' : 'bg-positive-soft text-positive';

  return (
    <div role="status" className={`mt-4 rounded-md px-4 py-3 text-[0.8125rem] ${tone}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Chip tone={posted === 0 ? 'warn' : 'positive'}>
          {written} {written === 1 ? 'transaction' : 'transactions'} posted
        </Chip>
        {outcome.refusedCount > 0 ? (
          <span>{outcome.refusedCount} still in the queue below, with the reason on the row.</span>
        ) : (
          <span>The queue is clear.</span>
        )}
        <button type="button" onClick={onDismiss} className="ml-auto underline">
          Dismiss
        </button>
      </div>

      {/*
       * THE SENTENCE THAT PREVENTS THE PANIC. A retry after a dropped connection posts nothing
       * new, and saying "3 transactions posted" for rows that were already in the ledger is how
       * somebody concludes they double-posted and starts reversing correct entries. So the count
       * above is what this run WROTE, and anything it merely found is named separately.
       */}
      {replayed > 0 ? (
        <p className="mt-1.5">
          {replayed} {replayed === 1 ? 'was' : 'were'} already in the ledger from an earlier attempt
          and {replayed === 1 ? 'was' : 'were'} not posted again — that is the retry working, not a
          duplicate.
        </p>
      ) : null}

      {posted > 0 ? (
        <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
          {outcome.posted.map((row) => (
            <span key={row.id} className="inline-flex items-center gap-1.5">
              <Ref entity="transaction" sequence={row.refSeq} />
              <Amount value={BigInt(row.amountCents)} currency tone="neutral" />
              {row.replayed ? <Chip tone="info">already posted</Chip> : null}
              {row.lateItem ? (
                <Chip tone="warn">late — {formatPeriod(row.periodStartsOn)}</Chip>
              ) : null}
            </span>
          ))}
        </p>
      ) : null}
    </div>
  );
}
